import { motion } from "framer-motion";
import { Check, ChevronLeft } from "lucide-react";

const DefaultBackIcon = ChevronLeft;

/**
 * Shared layout for conference registration: compact header, sidebar steps, main content.
 * Steps are clickable when they are "reachable" (stepIndex <= currentStep) so users can navigate back.
 */
export default function RegistrationLayout({
  title = "Conference Registration",
  subtitle,
  onBack,
  backLabel = "Back to Event",
  backIcon: BackIcon = DefaultBackIcon,
  onBackToEvent,
  backToEventLabel = "Back to event",
  theme = "orange", // "orange" | "teal"
  steps = [],
  currentStep,
  onStepClick,
  children,
  footer,
  className = "",
}) {
  const accent = theme === "teal" ? "teal" : "orange";
  const accentClasses = {
    orange: {
      stepActive: "bg-orange-500 border-orange-500 text-white",
      stepDone: "bg-orange-500 border-orange-500 text-white",
      stepPending: "border-gray-300 bg-white text-gray-400",
      hover: "hover:bg-orange-50 hover:border-orange-200",
      labelActive: "text-orange-600 font-semibold",
      labelDone: "text-orange-600",
      labelPending: "text-gray-500",
    },
    teal: {
      stepActive: "bg-teal-500 border-teal-500 text-white",
      stepDone: "bg-teal-500 border-teal-500 text-white",
      stepPending: "border-gray-300 bg-white text-gray-400",
      hover: "hover:bg-teal-50 hover:border-teal-200",
      labelActive: "text-teal-600 font-semibold",
      labelDone: "text-teal-600",
      labelPending: "text-gray-500",
    },
  };
  const c = accentClasses[accent];

  const isTeal = accent === "teal";
  const headerGradient = isTeal
    ? "from-teal-500/[0.12] via-cyan-500/[0.08] to-transparent"
    : "from-orange-500/[0.12] via-amber-500/[0.08] to-transparent";
  const badgeTone = isTeal ? "bg-teal-500/10 text-teal-700 border-teal-200/70" : "bg-orange-500/10 text-orange-700 border-orange-200/70";
  const backHover = isTeal ? "hover:bg-teal-500/10 hover:text-teal-700" : "hover:bg-orange-500/10 hover:text-orange-700";
  const linkColor = isTeal ? "text-teal-700 hover:text-teal-800" : "text-orange-700 hover:text-orange-800";

  return (
    <div className={`min-h-screen bg-slate-50 ${className}`}>
      {/* Header – below fixed navbar */}
      <header className="sticky top-16 z-30 mt-16 border-b border-white/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/75 sm:top-20 sm:mt-20">
        <div className={`pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-r ${headerGradient}`} aria-hidden />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:px-8">
          <div className="min-w-0 flex-1 text-left">
            <div className="mb-1 flex items-center justify-start">
              <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${badgeTone}`}>
                Conference Registration
              </span>
            </div>
            <h1 className="text-base font-bold tracking-tight text-gray-900 sm:text-xl">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-0.5 text-xs font-medium text-gray-600 sm:text-sm">
                {subtitle}
              </p>
            )}
          </div>
          <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto">
            <motion.button
              type="button"
              onClick={onBack}
              whileHover={{ x: -2 }}
              whileTap={{ scale: 0.98 }}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors sm:flex-none ${backHover}`}
            >
              <BackIcon size={20} strokeWidth={2} />
              <span className="sm:hidden">Back</span>
              <span className="hidden sm:inline">{backLabel}</span>
            </motion.button>
            {onBackToEvent ? (
              <motion.button
                type="button"
                onClick={onBackToEvent}
                whileHover={{ x: 2 }}
                whileTap={{ scale: 0.98 }}
                className={`shrink-0 flex-1 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-semibold shadow-sm transition-colors sm:flex-none ${linkColor}`}
              >
                {backToEventLabel}
              </motion.button>
            ) : null}
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl flex-col px-4 py-6 sm:px-6 lg:flex-row lg:gap-8 lg:px-8 lg:py-8">
        {/* Sidebar: steps */}
        {steps.length > 0 && (
          <aside className="mb-6 shrink-0 lg:sticky lg:top-[14rem] lg:mb-0 lg:w-56 lg:self-start xl:w-64">
            <nav
              className="rounded-2xl border border-gray-200/80 bg-white p-4 shadow-sm"
              aria-label="Registration steps"
            >
              <ul className="space-y-1">
                {steps.map((step, index) => {
                  const isActive = currentStep === step.number;
                  const isCompleted = currentStep > step.number;
                  const isReachable = currentStep >= step.number;
                  const canClick = isReachable && typeof onStepClick === "function";

                  return (
                    <li key={step.number}>
                      <button
                        type="button"
                        onClick={() => canClick && onStepClick(step.number)}
                        disabled={!canClick}
                        className={`flex w-full items-center gap-3 rounded-lg border-2 px-3 py-2.5 text-left transition-all ${
                          canClick ? c.hover : "cursor-default"
                        } ${
                          isActive
                            ? accent === "teal"
                              ? "border-teal-500 bg-teal-50 text-teal-900"
                              : "border-orange-500 bg-orange-50 text-orange-900"
                            : isCompleted
                            ? "border-transparent " + c.labelDone
                            : "border-gray-200 " + c.labelPending
                        }`}
                      >
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold ${
                            isCompleted
                              ? c.stepDone
                              : isActive
                              ? accent === "teal"
                                ? "border-teal-500 bg-teal-500 text-white"
                                : "border-orange-500 bg-orange-500 text-white"
                              : c.stepPending
                          }`}
                        >
                          {isCompleted ? (
                            <Check size={16} className="text-white" />
                          ) : (
                            index + 1
                          )}
                        </span>
                        <span className="min-w-0 truncate text-sm">
                          {step.shortLabel || step.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>
        )}

        {/* Main content */}
        <main className="min-w-0 flex-1">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="min-h-[320px]"
          >
            {children}
          </motion.div>

          {footer && (
            <div className="mt-6 rounded-xl border border-gray-200 bg-white px-6 py-4 shadow-sm sm:px-8">
              {footer}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
